import React, { useEffect, useState } from 'react';
import { Pool } from '../types';
import { apiService, formatters } from '../services/api';
import './ConnectPage.css';

const buildStratumUrls = (pool: Pool | null): string[] => {
  if (!pool) return [];
  if (pool.stratumUrls && pool.stratumUrls.length > 0) {
    return pool.stratumUrls;
  }

  if (pool.ports && pool.ports.length > 0) {
    return pool.ports.map(port => `stratum+tcp://kaspapool.cryptoware.uk:${port.port}`);
  }

  return ['stratum+tcp://kaspapool.cryptoware.uk:3112'];
};

const getPoolTicker = (pool?: Pool | null) => {
  if (!pool) return '';
  if (pool.coinSymbol) return pool.coinSymbol.toUpperCase();
  if (pool.coin) {
    const compact = pool.coin.replace(/[^a-z0-9]/gi, '').slice(0, 4);
    return compact ? compact.toUpperCase() : '';
  }
  return '';
};

const ConnectPage: React.FC = () => {
  const [pools, setPools] = useState<Pool[]>([]);
  const [selectedPoolId, setSelectedPoolId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  useEffect(() => {
    const loadPoolData = async () => {
      try {
        setLoading(true);
        setError(null);
        const poolData = await apiService.getPools();
        const availablePools = Array.isArray(poolData) ? poolData : [];

        if (!availablePools.length) {
          setError('No pools are available right now.');
          setPools([]);
          setSelectedPoolId('');
          return;
        }

        const storedPoolId = sessionStorage.getItem('connectPool');
        if (storedPoolId) {
          sessionStorage.removeItem('connectPool');
        }

        const preferredPool = availablePools.find(pool => pool.id === storedPoolId);
        const defaultPool = preferredPool || availablePools[0];
        setPools(availablePools);
        setSelectedPoolId(defaultPool.id);
      } catch (err) {
        setError('Unable to load pool information right now.');
      } finally {
        setLoading(false);
      }
    };

    loadPoolData();
  }, []);

  const activePool = pools.find(pool => pool.id === selectedPoolId) || null;
  const stratumUrls = buildStratumUrls(activePool);
  const defaultStratumUrl = stratumUrls[0] || 'stratum+tcp://kaspapool.cryptoware.uk:3112';
  const coinName = activePool?.coin || 'Kaspa';
  const coinSlug = activePool
    ? activePool.coin.toLowerCase().replace(/[^a-z0-9]/gi, '')
    : 'wallet';
  const exampleWallet = `${coinSlug}_address`;
  const exampleCommand = `${coinSlug}-miner -o ${defaultStratumUrl} -u ${exampleWallet}.rig1 -p x`;

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedUrl(value);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (copyErr) {
      console.error('Failed to copy value:', copyErr);
    }
  };

  return (
    <div className="connect-page">
      <section className="connect-hero">
        <div className="hero-heading">
          <div>
            <h2>Connect to our pool</h2>
            <p></p>
          </div>
          {pools.length > 0 && (
            <div className="coin-selector">
              <label htmlFor="connect-pool-select">Coin</label>
              <select
                id="connect-pool-select"
                value={selectedPoolId}
                onChange={(e) => setSelectedPoolId(e.target.value)}
              >
                {pools.map(pool => (
                  <option key={pool.id} value={pool.id}>
                    {pool.coin} ({getPoolTicker(pool)}) · {pool.paymentType || 'PPLNS'}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        {activePool && (
          <div className="hero-stats">
            <div className="hero-stat">
              <span className="hero-stat-label">Pool Hashrate</span>
              <span className="hero-stat-value">{formatters.hashrate(activePool.hashrate)}</span>
            </div>
            <div className="hero-stat">
              <span className="hero-stat-label">Payment Scheme</span>
              <span className="hero-stat-value">
                {activePool.paymentType || 'PPLNS'}
              </span>
            </div>
            <div className="hero-stat">
              <span className="hero-stat-label">Pool Fee</span>
              <span className="hero-stat-value">{activePool.fee}%</span>
            </div>
          </div>
        )}
      </section>

      {loading && (
        <div className="loading-container">
          <div className="loading" />
          <p>Loading connection details...</p>
        </div>
      )}

      {!loading && error && (
        <div className="error-message">{error}</div>
      )}

      {!loading && !error && activePool && (
        <>
          <section className="connection-grid">
            <div className="connection-card">
              <div className="card-header">
                <span>Stratum URLs</span>
                <p>Choose the closest location for best latency.</p>
              </div>
              <div className="stratum-urls">
                {stratumUrls.map((url) => (
                  <div key={url} className="stratum-url">
                    <code>{url}</code>
                    <button
                      className="copy-btn"
                      onClick={() => handleCopy(url)}
                      title="Copy URL"
                    >
                      📋
                    </button>
                    {copiedUrl === url && (
                      <span className="copy-feedback">Copied!</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="connection-card">
              <div className="card-header">
                <span>Available Ports</span>
                <p>All ports are vardiff. Use D=xxx for static diff.</p>
              </div>
              <div className="ports-list">
                {activePool.ports && activePool.ports.length > 0 ? (
                  activePool.ports.map(port => (
                    <div key={port.port} className="port-item">
                      <div className="port-meta">
                        <span className="port-number">{port.port}</span>
                        <span className="port-difficulty">Diff: {port.difficulty}</span>
                      </div>
                      {port.description && (
                        <span className="port-description">{port.description}</span>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="muted-text">Ports will appear once the pool configuration is finalized.</p>
                )}
              </div>
            </div>

            <div className="connection-card connection-info-card">
              <div className="card-header">
                <span>Connection Info</span>
                <p>Use your {coinName} wallet for payouts and optional worker names.</p>
              </div>
              <div className="connection-info">
                <div className="info-row">
                  <span className="info-label">Algorithm</span>
                  <span className="info-value">{activePool.algorithm || 'kHeavyHash'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Username</span>
                  <span className="info-value">WALLET_ADDRESS.worker</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Password</span>
                  <span className="info-value">x or D=xxx (static diff)</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Example</span>
                  <span className="info-value">
                    {exampleCommand}
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="connection-notes">
            <h4>Need a refresher?</h4>
            <ul>
              <li>Use your {coinName} wallet address as the username so we can track your shares.</li>
              <li>Worker names are optional and help you distinguish rigs (`wallet.worker`).</li>
              <li>Use `D=xxx` in the password field to request a static difficulty if vardiff isn’t desired.</li>
              <li>Monitor your miners on the Dashboard tab with the same wallet address.</li>
            </ul>
          </section>
        </>
      )}
    </div>
  );
};

export default ConnectPage;
